import Cookies from 'js-cookie';

declare global {
  interface Window {
    dataLayer: any;
    gtag: any;
    ttq?: {
      track?: (eventName: string, properties?: Record<string, unknown>, options?: Record<string, unknown>) => void;
      instance?: (pixelCode?: string) => { _i?: Record<string, unknown>; };
      _i?: Record<string, unknown>;
    };
  }
}

export interface TikTokContext {
  ttclid: string;
  ttp: string;
  first_url: string;
  page_url: string;
  referrer: string;
  pixel_ready: boolean;
}

const TIKTOK_CLICK_ID_COOKIE = 'ttclid';
const TIKTOK_PIXEL_COOKIE = '_ttp';
const TIKTOK_COOKIE_TTL_DAYS = 28;
const TIKTOK_PIXEL_WAIT_INTERVAL = 100;
const TIKTOK_PIXEL_MAX_ATTEMPTS = 30;
const TIKTOK_PIXEL_STORAGE_KEYS = ['_ttp'];

/**
 * 写入 TikTok 归因 Cookie，ttclid 需要至少保留 28 天并允许新值覆盖旧值
 */
function setTikTokCookie(name: string, value: string, days: number): void {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

/**
 * 读取 TikTok 归因 Cookie
 */
function getTikTokCookie(name: string): string {
  if (typeof document === 'undefined') return '';
  const cookieValue = document.cookie
    .split('; ')
    .find((item) => item.startsWith(`${name}=`))
    ?.split('=')[1];
  return cookieValue ? decodeURIComponent(cookieValue) : '';
}

/**
 * 读取 TikTok Pixel 写入的本地缓存，Pixel 可能先写 localStorage/sessionStorage 再同步 cookie
 */
function getTikTokStorageValue(keys: string[]): string {
  if (typeof window === 'undefined') return '';
  for (const key of keys) {
    try {
      const storageValue = window.localStorage.getItem(key) || window.sessionStorage.getItem(key);
      if (storageValue) return storageValue;
    } catch (error) {
      return '';
    }
  }
  return '';
}

/**
 * 读取 TikTok Pixel ID，优先取 Cookie，兜底取 Pixel 本地缓存
 */
function getTikTokPixelId(): string {
  return getTikTokCookie(TIKTOK_PIXEL_COOKIE) || getTikTokStorageValue(TIKTOK_PIXEL_STORAGE_KEYS);
}

/**
 * 检查 TikTok Pixel 是否已完成本地初始化
 */
export function isTikTokPixelInitialized(): boolean {
  if (typeof window === 'undefined') return false;
  return Boolean(window.ttq?._i && Object.keys(window.ttq._i).length);
}

/**
 * 保存 URL 中的 TikTok 点击 ID，新的 ttclid 会覆盖旧值
 */
export function persistTikTokClickId(): string {
  if (typeof window === 'undefined') return '';
  const ttclid = new URL(window.location.href).searchParams.get(TIKTOK_CLICK_ID_COOKIE) || '';
  if (ttclid) {
    setTikTokCookie(TIKTOK_CLICK_ID_COOKIE, ttclid, TIKTOK_COOKIE_TTL_DAYS);
  }
  return ttclid;
}

/**
 * 获取并兜底写入首次落地页 URL，供 TikTok 后端回传归因使用
 */
function getTikTokFirstUrl(): string {
  if (typeof window === 'undefined') return '';
  const firstUrl = window.localStorage.getItem('first_url');
  if (firstUrl) return firstUrl;
  window.localStorage.setItem('first_url', window.location.href);
  return window.location.href;
}

/**
 * 生成 TikTok 归因上下文，供埋点或后端回传透传使用
 */
export function buildTikTokContext(): TikTokContext {
  if (typeof window === 'undefined') {
    return {
      ttclid: '',
      ttp: '',
      first_url: '',
      page_url: '',
      referrer: '',
      pixel_ready: false,
    };
  }

  const ttclid = persistTikTokClickId();
  return {
    ttclid: ttclid || getTikTokCookie(TIKTOK_CLICK_ID_COOKIE),
    ttp: getTikTokPixelId(),
    first_url: getTikTokFirstUrl(),
    page_url: window.location.href,
    referrer: document.referrer,
    pixel_ready: isTikTokPixelInitialized(),
  };
}

/**
 * 异步采集 TikTok 归因上下文，等待 Pixel 初始化后尽量拿到 _ttp
 */
export function collectTikTokContext(callback?: (context: TikTokContext) => void): Promise<TikTokContext> {
  if (typeof window === 'undefined') {
    const context = buildTikTokContext();
    callback?.(context);
    return Promise.resolve(context);
  }

  persistTikTokClickId();

  return new Promise((resolve) => {
    let attempts = 0;
    const interval = window.setInterval(() => {
      attempts += 1;
      const context = buildTikTokContext();
      if (context.ttp || attempts >= TIKTOK_PIXEL_MAX_ATTEMPTS) {
        window.clearInterval(interval);
        callback?.(context);
        resolve(context);
      }
    }, TIKTOK_PIXEL_WAIT_INTERVAL);
  });
}

export interface ImageSize {
  width: number;
  height: number;
  base64: string;
}

/**
 * 通过file对象获取图片宽高
 *
 * @export
 * @param {File} file
 * @return {*}  {Promise<ImageSize>}
 */
export function getImgSizeByFile(file: File): Promise<ImageSize> {
  return new Promise((resolve, reject) => {
    let base64 = '';
    const img = new Image();
    img.onload = () => {
      console.log('width', img.naturalWidth, 'height', img.naturalHeight);
      resolve({
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
        base64,
      });
    };

    img.onerror = reject;

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      base64 = reader.result as string;
      img.src = reader.result as string;
    };
    reader.onerror = reject;
  });
}

/**
 * 获取图片信息
 * @param url 图片地址
 * @returns 图片信息（宽高等）
 */
/**
 * 判断 URL 是否指向视频资源（忽略 query 参数后再匹配扩展名）。
 */
export function isVideoUrl(url: string): boolean {
  return /\.(mp4|mov|avi|mkv|webm|ogg|m4v|flv|wmv)$/i.test(url.split('?')[0]);
}

/**
 * 判断 URL 是否指向 PAG 动画资源（忽略 query 参数后再匹配扩展名）。
 */
export function isPagUrl(url: string): boolean {
  if (!url) return false;
  return /\.pag$/i.test(url.split('?')[0]);
}

export async function getImageElementByURL(url: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.src = url;
    image.onload = () => {
      resolve(image);
    };
    image.onerror = () => {
      reject();
    };
  });
}

/**
 * 获取视频信息
 * @param url 视频地址（可以是 blob URL、远程地址等）
 * @returns 包含视频宽高的 HTMLVideoElement
 */
export function getVideoElementByURL(url: string): Promise<HTMLVideoElement> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.src = url;
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      resolve(video);
    };

    video.onerror = (err) => {
      reject(new Error('Failed to load video metadata'));
    };
  });
}

// 获取视频封面宽高
interface VideoInfo {
  width: number;
  height: number;
  duration: number;
  firstFrameUrl: string;
}

/**
 * 通过file对象获取视频封面宽高
 *
 * @export
 * @param {File} file
 * @return {*}  {Promise<VideoInfo>}
 */
export function getVideoCoverSizeByFile(file: File): Promise<VideoInfo> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      const info: VideoInfo = {
        width: video.videoWidth,
        height: video.videoHeight,
        duration: video.duration,
        firstFrameUrl: URL.createObjectURL(file),
      };

      // 清理资源
      URL.revokeObjectURL(video.src);
      resolve(info);
    };

    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      reject(new Error('无法加载视频'));
    };

    video.src = URL.createObjectURL(file);
  });
}

/**
 * 从埋点web sdk中取DeviceId当做gid
 * @returns
 */
export const getGid = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  try {
    let gid = Cookies.get('gid') || '';
    if (!gid) {
      // eslint-disable-next-line no-underscore-dangle
      gid = (window.mtstat as any).getStatsMarker() || ''; // TODO: 替换getStatsMarker
      Cookies.set('gid', gid, { expires: 30 });
    }
    return gid;
  } catch (error) {
    console.log('getGid error===', error);
    return '';
  }
};

/**
 * 判断是否为windows系统
 *
 * @export
 * @return {*}
 */
export function isWindows() {
  return navigator.userAgent.includes('Windows');
}

/**
 * 替换模板中的 {xxx} 占位符
 * @param template - 字符串模板
 * @param params - 替换参数
 */
export function replaceTemplateParams(template: string, params?: Record<string, any>): string {
  if (!params) return template;

  return template.replace(/\{(\w+)\}/g, (_, key) => {
    return params[key] ?? '';
  });
}

/**
 * 获取当前设备类型
 *
 * @returns 是否是安卓系统
 */
export function checkIsAndroid() {
  const { userAgent } = navigator;
  let isAndroid = false;
  if (userAgent.indexOf('android') >= 0 || userAgent.indexOf('Android') >= 0 || userAgent.indexOf('Linux') >= 0) {
    isAndroid = true;
  } else {
    isAndroid = false;
  }
  return isAndroid;
}

/**
 * 判断 URL 是否是图片链接，并返回图片后缀（如 "png"、"jpg"）
 * @param url 要检查的 URL
 * @returns { extension: string | null, isImage: boolean }
 */
export function getImageExtensionFromUrl(url: string): { extension: string | null; isImage: boolean; } {
  try {
    const parsedUrl = new URL(url);
    const pathname = parsedUrl.pathname.toLowerCase();

    // 匹配常见的图片后缀（可自行扩展）
    const match = pathname.match(/\.(png|jpe?g|gif|webp|svg|bmp|ico|tiff?)$/i);

    if (match) {
      return { extension: match[1], isImage: true }; // 返回小写的扩展名（如 "png"）
    }
    return { extension: null, isImage: false };
  } catch (err) {
    return { extension: null, isImage: false }; // 无效的 URL
  }
}

// 在 utils 中添加
export function checkIsWeChat() {
  return /MicroMessenger/i.test(navigator.userAgent);
}

/**
 * 判断是否为移动端
 *
 * @export
 * @param {string} [userAgent]
 * @return {*}
 */
export function isMobile(userAgent?: string) {
  const ua = userAgent ? userAgent.toLowerCase() : navigator.userAgent.toLowerCase();
  return /iphone|ipod|android.*mobile|mobile|windows phone|micromessenger/i.test(ua);
}

/**
 * key为string类型的Map转对象
 *
 * @export
 * @param {Map<string, unknown>} strMap
 * @return {*}
 */
export function strMapToObj(strMap: Map<string, unknown>) {
  const obj = Object.create(null);
  for (const [k, v] of strMap) {
    obj[k] = v;
  }
  return obj;
}

/**
 * 谷歌分析事件上报工具
 */

interface GTagEvent {
  /**
   * 事件回调函数
   */
  event_callback?: () => void;
  /**
   * 回调函数超时时间（毫秒）
   */
  event_timeout?: number;
  /**
   * 允许添加其他自定义参数
   */
  [key: string]: any;
}

/**
 * 发送谷歌分析事件
 * @param eventName 事件名称
 * @param params 事件参数
 */
export const trackGoogleEvent = (eventName: string, params?: GTagEvent): void => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  }
};

/**
 *  发送meta 分析事件
 */

export const trackMetaEvent = (eventName: 'track' | 'trackCustom', eventKey: string, params?: Record<string, string>): void => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq(eventName, eventKey, params);
  }
};

/**
 * 发送 TikTok Pixel 标准事件
 */
export const trackTikTokEvent = (
  eventName: string,
  properties?: Record<string, unknown>,
  options?: Record<string, unknown>,
): void => {
  if (typeof window !== 'undefined' && window.ttq?.track) {
    window.ttq.track(eventName, properties, options);
  }
};

/**
 * 休眠
 *
 * @export
 * @param {number} ms
 * @return {*}
 */
export function sleep(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}
